// src/components/sat/ReadAloud.tsx
'use client'

import { useCallback, useEffect, useRef, type RefObject } from 'react'
import { useSpeech } from '@/hooks/use-speech'
import {
  splitSentences,
  readAloudContent,
  spokenSentence,
  type Sentence,
} from '@/lib/sat/read-aloud'
import { rangeFromOffsets } from '@/lib/sat/highlight-dom'

const HIGHLIGHT_NAME = 'sat-reading'

// CSS Custom Highlight API (Baseline 2025). Used as an OVERLAY for the
// read-along sentence highlight, so it never touches the #778 user <mark>s and
// degrades to "no visual highlight, audio still works" where unsupported.
function highlightRegistry(): Map<string, unknown> | null {
  const c =
    typeof CSS !== 'undefined' ? (CSS as unknown as { highlights?: Map<string, unknown> }) : null
  const HighlightCtor =
    typeof window !== 'undefined'
      ? (window as unknown as { Highlight?: unknown }).Highlight
      : undefined
  return c?.highlights && HighlightCtor ? c.highlights : null
}

function setReadingHighlight(root: HTMLElement, sentence: Sentence | undefined): void {
  const reg = highlightRegistry()
  if (!reg) return
  if (!sentence) {
    reg.delete(HIGHLIGHT_NAME)
    return
  }
  const range = rangeFromOffsets(root, sentence.start, sentence.end)
  const HighlightCtor = (window as unknown as { Highlight: new (r: Range) => unknown }).Highlight
  if (range) reg.set(HIGHLIGHT_NAME, new HighlightCtor(range))
  else reg.delete(HIGHLIGHT_NAME)
}

function clearReadingHighlight(): void {
  highlightRegistry()?.delete(HIGHLIGHT_NAME)
}

/**
 * Bluebook-style read-aloud control (#791). Reads the current question content
 * (passage + question + choices, taken from `rootRef`'s textContent) on the
 * on-device Web Speech API, with play / pause / stop, a speed control, and
 * read-along sentence highlighting. Renders nothing where TTS is unsupported.
 */
export default function ReadAloud({
  rootRef,
  reader = false,
}: {
  rootRef: RefObject<HTMLElement | null>
  /** #830: the student has the reader accommodation — surface a "Reader" badge
   *  so the read-aloud reads as an enabled accommodation, not just a tool. */
  reader?: boolean
}) {
  const { supported, status, index, rate, speak, pause, resume, stop, setRate } = useSpeech()
  const sentencesRef = useRef<Sentence[]>([])

  // Sync the read-along highlight to the sentence being spoken.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    setReadingHighlight(root, index >= 0 ? sentencesRef.current[index] : undefined)
  }, [index, rootRef])

  // Clear the highlight when the control unmounts (e.g. on question change).
  useEffect(() => () => clearReadingHighlight(), [])

  const start = useCallback(() => {
    const root = rootRef.current
    if (!root) return
    // Split over the container's own text, so sentence offsets keep pointing
    // at the same DOM positions and the read-along highlight is untouched.
    // Only what we SAY differs: superscripts become words.
    const { text, subs } = readAloudContent(root)
    const sentences = splitSentences(text)
    sentencesRef.current = sentences
    speak(sentences.map((s) => spokenSentence(s, subs)))
  }, [rootRef, speak])

  if (!supported) return null

  const btn =
    'inline-flex h-9 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-800 hover:bg-slate-100'

  return (
    <div
      className="flex items-center gap-1.5"
      role="group"
      aria-label={reader ? 'Read aloud (reader accommodation enabled)' : 'Read aloud'}
    >
      {reader && (
        <span
          className="rounded bg-brand/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-ink"
          role="status"
          aria-label="Reader accommodation enabled"
          title="Reader accommodation enabled"
        >
          Reader
        </span>
      )}
      {status === 'idle' ? (
        <button type="button" className={btn} onClick={start} aria-label="Read the question aloud">
          Read aloud
        </button>
      ) : (
        <>
          {status === 'speaking' ? (
            <button type="button" className={btn} onClick={pause} aria-label="Pause reading">
              Pause
            </button>
          ) : (
            <button type="button" className={btn} onClick={resume} aria-label="Resume reading">
              Resume
            </button>
          )}
          <button type="button" className={btn} onClick={stop} aria-label="Stop reading">
            Stop
          </button>
          <label htmlFor="sat-tts-rate" className="sr-only">
            Reading speed
          </label>
          <select
            id="sat-tts-rate"
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            aria-label="Reading speed"
            className="h-9 rounded-md border border-slate-300 bg-white px-1 text-xs text-slate-800"
          >
            <option value={0.75}>0.75×</option>
            <option value={1}>1×</option>
            <option value={1.25}>1.25×</option>
            <option value={1.5}>1.5×</option>
          </select>
        </>
      )}
    </div>
  )
}
